import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOtherSettings } from '@namespace/cms';
import { FILTER_ALL_VALUE } from '@namespace/common';
import { useI18n } from '@namespace/i18n';
import { FilterField } from '../../types/history';
import { getFilterFieldsConfig } from '../../utils/history';
import { useUserPaymentMethodsHistory } from '../useUserPaymentMethodsHistory';

type AmountFilterFilterOption = {
  value: string;
  label: string;
};

type UseTransactionsFilterParams = {
  amountFilterOptions: AmountFilterFilterOption[];
  type: 'deposit' | 'withdraw';
  filterFields?: FilterField[];
};

export type Filter = {
  amount: string;
  dateOptionId: string;
  endDate: string;
  startDate: string;
  status: string;
  wallet: string;
};

export const useTransactionsFilter = ({
  amountFilterOptions,
  type,
  filterFields
}: UseTransactionsFilterParams) => {
  const { t, f } = useI18n();
  const { payments } = useOtherSettings();

  const minDateFromOffset: number | undefined =
    payments?.transactionFilters?.customDateRange?.minDateFromOffset;

  const maxMinDate = useMemo(
    () =>
      minDateFromOffset
        ? {
            seconds: Math.abs(minDateFromOffset)
          }
        : undefined,
    [minDateFromOffset]
  );

  const DATE_FORMATS_TYPES = f.getFormats();

  const initialFilterState = useMemo(() => {
    const startDate = { days: 1 };
    const endDate = { days: 1 };

    return {
      status: FILTER_ALL_VALUE,
      dateOptionId: '2',
      startDate: f
        .getDateTime()
        .startOf('day')
        .minus(startDate)
        .toFormat(DATE_FORMATS_TYPES.API),
      endDate: f.getDateTime().plus(endDate).toFormat(DATE_FORMATS_TYPES.API),
      wallet: FILTER_ALL_VALUE,
      amount: FILTER_ALL_VALUE
    };
  }, [DATE_FORMATS_TYPES.API, f]);

  const [filter, setFilter] = useState<Filter>(initialFilterState);

  useEffect(() => {
    if (type) {
      setFilter(initialFilterState);
    }
  }, [initialFilterState, type]);

  const usedSidList = useUserPaymentMethodsHistory();

  const getFieldsConfig = useCallback(
    () =>
      getFilterFieldsConfig({
        t,
        usedSidList,
        type,
        fields: filterFields,
        amountFilterOptions,
        maxMinDate
      }),
    [amountFilterOptions, filterFields, maxMinDate, t, type, usedSidList]
  );

  return {
    getFieldsConfig,
    filter,
    setFilter
  };
};
